import React, { useCallback, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup from "../../../shared/Popup/ToolPrepPopup";
import EnhancePreview from "./EnhancePreview";
import EnhanceSourceCards from "./EnhanceSourceCards";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import Tooltip from "../../../../../components/common/Tooltip/Tooltip";
import { useToolPrepImageSource } from "../../../hooks/useToolPrepImageSource";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import { TOOL_PREP_FOOTER_HINT, TOOL_PREP_RESULT_FOOTER_HINT, TOOL_PREP_PREPARING_PREVIEW_LABEL } from "../../../../../utils/editorEntrySession";
import { useToolPrepImageDownload } from "../../../hooks/useToolPrepImageDownload";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import { resolveCreationImageUrl } from "../../../../../utils/resolveCreationImageUrl";
import { resolveImageSource } from "../../../../../utils/resolveImageSource";
import { enhanceSingle } from "../../../../../services/api/tools/enhance";
import "./EnhanceToolPrep.css";

const STRENGTH_PRESETS = [
  {
    id: "subtle",
    label: "Subtle",
    sub: "Light cleanup",
    tip: (
      <div className="etp-strength-tip">
        <p className="etp-strength-tip__title">
          Subtle — &ldquo;Light cleanup&rdquo;
        </p>
        <ul className="etp-strength-tip__list">
          <li>Light sharpening and mild noise reduction</li>
          <li>Closest to the original look</li>
          <li>
            Best when the photo is already decent and you only want a gentle
            polish
          </li>
          <li>Lowest risk of looking over-processed</li>
        </ul>
      </div>
    ),
  },
  {
    id: "balanced",
    label: "Balanced",
    sub: "Recommended",
    tip: (
      <div className="etp-strength-tip">
        <p className="etp-strength-tip__title">
          Balanced — &ldquo;Recommended&rdquo;
        </p>
        <ul className="etp-strength-tip__list">
          <li>Medium sharpening, clarity, and detail recovery</li>
          <li>Noticeable improvement without looking artificial</li>
          <li>
            Good default for most product shots, portraits, and everyday photos
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: "strong",
    label: "Strong",
    sub: "Max detail",
    tip: (
      <div className="etp-strength-tip">
        <p className="etp-strength-tip__title">
          Strong — &ldquo;Max detail&rdquo;
        </p>
        <ul className="etp-strength-tip__list">
          <li>
            Heaviest enhancement: more edge sharpness, contrast, and recovered
            fine detail
          </li>
          <li>
            Best for soft, blurry, or low-quality sources that need a bigger
            lift
          </li>
          <li>
            Higher chance of looking &ldquo;AI-processed&rdquo; (halos, crunchy
            textures, exaggerated skin/fabric)
          </li>
        </ul>
      </div>
    ),
  },
];

const DEFAULT_STRENGTH = "balanced";

/**
 * AI Tools → AI Enhance prep.
 * Strength maps to Photoroom AI Relight. A custom prompt is not sent.
 */
const EnhanceToolPrep = ({
  originalImageUrl = null,
  imageFile = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const { getToken } = useAuth();
  const [stageKey, setStageKey] = useState("original");
  const [resultImageUrl, setResultImageUrl] = useState(null);
  const [creationId, setCreationId] = useState(null);
  const [strengthId, setStrengthId] = useState(DEFAULT_STRENGTH);
  const [error, setError] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const scrollBodyRef = useRef(null);
  const leftPanelRef = useRef(null);

  const clearResult = useCallback(() => {
    setResultImageUrl(null);
    setCreationId(null);
  }, []);

  const {
    sourceImageUrl,
    resolvedFile,
    sourceFileName,
    sourceFileSizeLabel,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: genLoading,
    onSourceChange: () => {
      clearResult();
      setStageKey("original");
      setError(null);
    },
  });

  const canGenerate =
    Boolean(sourceImageUrl) && !genLoading;

  const { downloadBusy, onDownload, downloadFlow } = useToolPrepImageDownload(
    resultImageUrl,
    sourceFileName,
    {
      enabled: Boolean(resultImageUrl),
      fallbackName: "enhanced.png",
      onError: setError,
      toolKey: "ai_enhance",
      shareTitle: "AI Enhance",
    }
  );

  const stageImageUrl =
    stageKey === "preview" && resultImageUrl
      ? resultImageUrl
      : sourceImageUrl;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setGenLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Sign in required.");
      const { file, image_url } = await resolveImageSource({
        sourceUrl: sourceImageUrl,
        resolvedFile,
        filename: "enhance-input.png",
        token,
      });
      const data = await enhanceSingle(token, file, {
        strength: strengthId,
        output_format: "webp",
        ...(image_url ? { image_url } : {}),
      });
      const outputUrl = data?.output_url;
      if (!outputUrl) {
        throw new Error(data?.message || data?.detail || "Generation failed.");
      }
      const { displayUrl, creationId: cid } = await resolveCreationImageUrl(
        token,
        outputUrl,
        data.creation_id
      );
      setResultImageUrl(displayUrl);
      setCreationId(cid ?? data.creation_id ?? null);
      setStageKey("preview");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Generation failed. Please try again.";
      setError(typeof msg === "string" ? msg : "Generation failed.");
    } finally {
      setGenLoading(false);
    }
  };

  const handleClose = () => {
    if (genLoading || downloadBusy) return;
    onCancel();
  };

  const handleOpenInEditor = async () => {
    if (!resultImageUrl) return;
    try {
      await onDone({
        imageUrl: resultImageUrl,
        creationId,
      });
    } catch (err) {
      setError(err?.message || "Could not open result in editor.");
    }
  };

  const leftContent = (
    <div className="etp-panel" ref={leftPanelRef}>
      <div className="etp-scroll tool-prep-scroll" ref={scrollBodyRef}>
        <div className="etp-source">
          <EnhanceSourceCards
            selectedKey={stageKey}
            onSelect={setStageKey}
            originalImageUrl={sourceImageUrl}
            previewImageUrl={resultImageUrl}
            onUploadClick={openUploadPicker}
            onRemove={clearSource}
            uploadProgress={uploadProgress}
            sourceName={sourceFileName}
            sourceSizeLabel={sourceFileSizeLabel}
            actionsDisabled={genLoading}
            hasResult={Boolean(resultImageUrl)}
          />
        </div>

        <div className="etp-section">
          <p className="etp-label">Strength</p>
          <div
            className="etp-strength-grid"
            role="listbox"
            aria-label="Enhance strength"
          >
            {STRENGTH_PRESETS.map((preset) => {
              const selected = strengthId === preset.id;
              return (
                <Tooltip
                  key={preset.id}
                  content={preset.tip}
                  position="top"
                  size="panel"
                  block
                  flip={false}
                  fillBoundary
                  boundaryRef={leftPanelRef}
                >
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`etp-strength-card${selected ? " is-selected" : ""}`}
                    onClick={() => {
                      setStrengthId(preset.id);
                      setError(null);
                    }}
                    disabled={genLoading}
                  >
                    <span className="etp-strength-card__title">{preset.label}</span>
                    <span className="etp-strength-card__sub">{preset.sub}</span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
          <p className="etp-label">
            Subtle keeps the original color. Balanced and Strong correct the exposure.
          </p>
        </div>

        {error ? <p className="etp-error">{error}</p> : null}
      </div>
    </div>
  );

  useSyncScrollbarWidth(scrollBodyRef, [sourceImageUrl, stageKey, strengthId]);

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <ToolPrepPopup
        toolKey="ai_enhance"
        title="AI Enhance"
        left={leftContent}
        preview={
          <EnhancePreview
            imageUrl={stageImageUrl}
            loading={genLoading}
            loadingLabel="Enhancing…"
            emptyLabel={
              uploadProgress != null && !stageImageUrl
                ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                : undefined
            }
          />
        }
        onDone={handleGenerate}
        onCancel={handleClose}
        confirmClose={Boolean(sourceImageUrl || resultImageUrl)}
        onDownload={onDownload}
        onOpenInEditor={resultImageUrl ? handleOpenInEditor : undefined}
        busy={genLoading || downloadBusy}
        doneDisabled={!canGenerate}
        doneLabel={resultImageUrl ? "Regenerate" : "Generate"}
        cancelLabel="Close"
        footerHint={
          error ||
          (resultImageUrl
            ? TOOL_PREP_RESULT_FOOTER_HINT
            : sourceImageUrl
              ? TOOL_PREP_FOOTER_HINT
              : "Upload an image, pick a strength, then generate.")
        }
        footerHintClassName={
          error ? "tool-prep-popup__footer-hint--error" : undefined
        }
        bodyOwnsScroll
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey="ai_enhance"
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default EnhanceToolPrep;
